import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaApple, FaGooglePlay, FaChevronDown, FaDumbbell, FaUsers, FaChartLine, FaMobileAlt, FaPlay, FaTrophy, FaStar, FaTimes, FaEnvelope, FaCheckCircle } from 'react-icons/fa';
import { useInView } from 'react-intersection-observer';
import axios from 'axios';
import { API_URL } from '../config/apiConfig';

// Import assets
import heroVideo from '../assets/videos/hero-gym-dumbbells.mp4';
import workoutImg1 from '../assets/images/workout-training-1.jpg';
import workoutImg2 from '../assets/images/workout-training-2.jpg';
import workoutImg3 from '../assets/images/workout-training-3.jpg';
import workoutImg4 from '../assets/images/workout-training-4.jpg';
import workoutImg5 from '../assets/images/workout-training-5.jpg';
import workoutImg6 from '../assets/images/workout-training-6.jpg';
import appScreenshot1 from '../assets/images/app-screenshot-1.jpg';
import appScreenshot2 from '../assets/images/app-screenshot-2.jpg';
import appScreenshot3 from '../assets/images/app-screenshot-3.jpg';
import appScreenshot4 from '../assets/images/app-screenshot-4.jpg';
import transformation1 from '../assets/images/transformation-1.jpg';
import transformation2 from '../assets/images/transformation-2.jpg';
import transformation3 from '../assets/images/transformation-3.jpg';
import transformation4 from '../assets/images/transformation-4.jpg';
import workoutVideo1 from '../assets/videos/workout-cardio-1.mp4';
import workoutVideo2 from '../assets/videos/workout-strength-12.mp4';
import workoutVideo3 from '../assets/videos/workout-hiit-1.mp4';
import trainingVideo1 from '../assets/videos/training-session-11.mp4';
import trainingVideo2 from '../assets/videos/training-session-2.mp4';
import trainingVideo3 from '../assets/videos/training-session-3.mp4';

const HomePage = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterName, setNewsletterName] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [newsletterError, setNewsletterError] = useState('');
  const [ref1, inView1] = useInView({ threshold: 0.1, triggerOnce: true });
  const [ref2, inView2] = useInView({ threshold: 0.1, triggerOnce: true });
  const [ref3, inView3] = useInView({ threshold: 0.1, triggerOnce: true });
  const [ref4, inView4] = useInView({ threshold: 0.1, triggerOnce: true });
  const [ref5, inView5] = useInView({ threshold: 0.1, triggerOnce: true });
  const [ref6, inView6] = useInView({ threshold: 0.1, triggerOnce: true });

  const features = [
    {
      icon: <FaDumbbell size={40} />,
      title: 'Expert Coaching',
      description: 'Connect with certified fitness coaches who create personalized workout plans tailored to your goals and fitness level.',
      image: workoutImg1,
    },
    {
      icon: <FaUsers size={40} />,
      title: 'Community Support',
      description: 'Join a thriving community of fitness enthusiasts. Share progress, get motivated, and celebrate victories together.',
      image: workoutImg2,
    },
    {
      icon: <FaChartLine size={40} />,
      title: 'Track Progress',
      description: 'Advanced analytics and tracking tools help you monitor your fitness journey with detailed insights and metrics.',
      image: workoutImg3,
    },
    {
      icon: <FaMobileAlt size={40} />,
      title: 'Train Anywhere',
      description: 'Access your workouts, nutrition plans, and coaching sessions anytime, anywhere with our mobile-first platform.',
      image: workoutImg4,
    },
    {
      icon: <FaTrophy size={40} />,
      title: 'Achieve Goals',
      description: 'Set and crush your fitness goals with structured programs, milestone tracking, and achievement rewards.',
      image: workoutImg5,
    },
    {
      icon: <FaStar size={40} />,
      title: 'Premium Content',
      description: 'Access exclusive workout videos, nutrition guides, and wellness content from industry-leading experts.',
      image: workoutImg6,
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Download & Sign Up',
      description: 'Get started in seconds. Download the app, create your profile, and set your fitness goals.',
      image: appScreenshot1,
    },
    {
      number: '02',
      title: 'Find Your Coach',
      description: 'Browse certified coaches, view their specialties, and book your first session with the perfect match.',
      image: appScreenshot2,
    },
    {
      number: '03',
      title: 'Start Training',
      description: 'Follow personalized workout plans, track your progress, and stay connected with your coach.',
      image: appScreenshot3,
    },
    {
      number: '04',
      title: 'Transform Your Life',
      description: 'Watch your body transform, hit milestones, and become the best version of yourself.',
      image: appScreenshot4,
    },
  ];

  const videos = [
    { id: 1, title: 'Cardio Blast', thumbnail: workoutImg1, video: workoutVideo1, duration: '15 min' },
    { id: 2, title: 'Strength Training', thumbnail: workoutImg2, video: workoutVideo2, duration: '20 min' },
    { id: 3, title: 'HIIT Workout', thumbnail: workoutImg3, video: workoutVideo3, duration: '12 min' },
    { id: 4, title: 'Core Power', thumbnail: workoutImg4, video: trainingVideo1, duration: '18 min' },
    { id: 5, title: 'Full Body', thumbnail: workoutImg5, video: trainingVideo2, duration: '25 min' },
    { id: 6, title: 'Mobility Flow', thumbnail: workoutImg6, video: trainingVideo3, duration: '10 min' },
  ];

  const transformations = [
    {
      name: 'Sarah Johnson',
      achievement: 'Lost 45 lbs in 6 months',
      image: transformation1,
      quote: 'MyFitness changed my life. The coaches are incredible and the community support is unmatched!',
      rating: 5,
    },
    {
      name: 'Mike Chen',
      achievement: 'Gained 20 lbs muscle',
      image: transformation2,
      quote: 'Best investment I ever made. The personalized training plans delivered real results.',
      rating: 5,
    },
    {
      name: 'Emily Rodriguez',
      achievement: 'Completed first marathon',
      image: transformation3,
      quote: 'From couch to marathon runner! The structured programs and expert guidance made it possible.',
      rating: 5,
    },
    {
      name: 'David Thompson',
      achievement: 'Transformed in 90 days',
      image: transformation4,
      quote: 'The accountability and motivation from my coach kept me on track. Results speak for themselves!',
      rating: 5,
    },
  ];

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterLoading(true);
    setNewsletterError('');

    try {
      await axios.post(`${API_URL}/newsletter/subscribe`, {
        email: newsletterEmail,
        name: newsletterName,
        source: 'website',
      });

      setNewsletterSuccess(true);
      setNewsletterEmail('');
      setNewsletterName('');
      
      setTimeout(() => {
        setNewsletterSuccess(false);
      }, 5000);
    } catch (error) {
      setNewsletterError(
        error.response?.data?.message || 'Failed to subscribe. Please try again.'
      );
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <div className="bg-black">
      {/* HERO SECTION */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src={heroVideo} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/90"></div>
        </div>

        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-block mb-6"
              >
                <div className="glass px-6 py-2 rounded-full">
                  <span className="text-primary font-semibold">🔥 #1 Fitness App</span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-5xl md:text-7xl lg:text-8xl font-black mb-6 leading-tight"
              >
                Transform Your Body.
                <br />
                <span className="gradient-text">Elevate Your Lifestyle.</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto"
              >
                Connect with expert coaches, track your progress, and join a community
                of fitness enthusiasts. Your transformation starts here.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="flex flex-col sm:flex-row items-center justify-center gap-4"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary flex items-center space-x-3 text-lg animate-pulse-glow"
                >
                  <FaApple size={24} />
                  <div className="text-left">
                    <div className="text-xs">Download on the</div>
                    <div className="font-bold">App Store</div>
                  </div>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-secondary flex items-center space-x-3 text-lg"
                >
                  <FaGooglePlay size={24} />
                  <div className="text-left">
                    <div className="text-xs">Get it on</div>
                    <div className="font-bold">Google Play</div>
                  </div>
                </motion.button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="mt-16 grid grid-cols-3 gap-8 max-w-2xl mx-auto"
              >
                {[
                  { number: '500K+', label: 'Active Users' },
                  { number: '1000+', label: 'Expert Coaches' },
                  { number: '4.9★', label: 'App Rating' },
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl md:text-4xl font-bold text-primary">
                      {stat.number}
                    </div>
                    <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-10"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="flex flex-col items-center cursor-pointer"
          >
            <span className="text-white text-sm mb-2">Scroll Down</span>
            <FaChevronDown className="text-primary text-2xl" />
          </motion.div>
        </motion.div>
      </section>

      {/* FEATURES SECTION */}
      <section ref={ref1} className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView1 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6">
              Everything You Need to <span className="gradient-text">Succeed</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Powerful features designed to help you achieve your fitness goals faster and more effectively.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView1 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="glass rounded-2xl overflow-hidden group cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-primary">
                    {feature.icon}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-400">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section ref={ref2} className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView2 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6">
              Your Journey to <span className="gradient-text">Greatness</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Four simple steps to transform your body and elevate your lifestyle.
            </p>
          </motion.div>

          <div className="space-y-24">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={inView2 ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } items-center gap-12`}
              >
                <div className="flex-1">
                  <div className="text-8xl font-black text-primary/20 mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold mb-4">{step.title}</h3>
                  <p className="text-xl text-gray-400 leading-relaxed">
                    {step.description}
                  </p>
                </div>
                <div className="flex-1">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="relative rounded-2xl overflow-hidden shadow-2xl"
                  >
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-auto"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* APP PREVIEW SECTION */}
      <section ref={ref3} className="py-24 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary rounded-full filter blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView3 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6">
              Experience the <span className="gradient-text">App</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Intuitive design meets powerful functionality. See why millions choose MyFitness.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[appScreenshot1, appScreenshot2, appScreenshot3, appScreenshot4].map((screenshot, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView3 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.05 }}
                className="relative group cursor-pointer"
              >
                <div className="glass rounded-3xl p-4 overflow-hidden">
                  <img
                    src={screenshot}
                    alt={`App Screenshot ${index + 1}`}
                    className="w-full h-auto rounded-2xl"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"></div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView3 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-center mt-12"
          >
            <button className="btn-primary text-lg">
              Download Now
            </button>
          </motion.div>
        </div>
      </section>

      {/* VIDEO SHOWCASE SECTION */}
      <section ref={ref4} className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView4 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6">
              Premium <span className="gradient-text">Workout Videos</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Access thousands of expert-led workout videos. From beginner to advanced, we've got you covered.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView4 ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="relative group cursor-pointer rounded-2xl overflow-hidden"
                onClick={() => setActiveVideo(video)}
              >
                <div className="relative h-64">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-colors duration-300"></div>
                  
                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      whileHover={{ scale: 1.2 }}
                      className="w-16 h-16 bg-primary rounded-full flex items-center justify-center"
                    >
                      <FaPlay className="text-white ml-1" size={24} />
                    </motion.div>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
                    <h3 className="text-xl font-bold mb-1">{video.title}</h3>
                    <p className="text-sm text-gray-300">{video.duration}</p>
                  </div>

                  <div className="absolute top-4 right-4 bg-black/70 px-3 py-1 rounded-full text-sm">
                    {video.duration}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO MODAL */}
      <AnimatePresence>
        {activeVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            onClick={() => setActiveVideo(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="relative w-full max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setActiveVideo(null)}
                className="absolute -top-12 right-0 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-10"
              >
                <FaTimes size={20} />
              </button>

              {/* Video Player */}
              <div className="glass rounded-2xl overflow-hidden">
                <video
                  autoPlay
                  controls
                  className="w-full h-auto"
                  src={activeVideo.video}
                >
                  <source src={activeVideo.video} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                
                {/* Video Info */}
                <div className="p-6 bg-gradient-to-t from-black to-transparent">
                  <h3 className="text-2xl font-bold mb-2">{activeVideo.title}</h3>
                  <div className="flex items-center gap-4 text-gray-400">
                    <span className="flex items-center gap-2">
                      <FaPlay size={14} />
                      {activeVideo.duration}
                    </span>
                    <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm font-semibold">
                      Workout Video
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* TRANSFORMATIONS SECTION */}
      <section ref={ref5} className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView5 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6">
              Real People. <span className="gradient-text">Real Results.</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Join thousands who have transformed their lives with MyFitness. Your success story starts here.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {transformations.map((transformation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView5 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="glass rounded-2xl overflow-hidden"
              >
                <div className="relative h-80">
                  <img
                    src={transformation.image}
                    alt={transformation.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(transformation.rating)].map((_, i) => (
                        <FaStar key={i} className="text-primary" />
                      ))}
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{transformation.name}</h3>
                    <p className="text-primary font-semibold mb-3">{transformation.achievement}</p>
                    <p className="text-gray-300 italic">"{transformation.quote}"</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView5 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-center mt-12"
          >
            <button className="btn-primary text-lg">
              Start Your Transformation
            </button>
          </motion.div>
        </div>
      </section>

      {/* NEWSLETTER SECTION */}
      <section ref={ref6} className="py-24 bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView6 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-primary to-secondary mb-6">
              <FaEnvelope className="text-white text-3xl" />
            </div>
            
            <h2 className="text-4xl md:text-5xl font-black mb-4 text-white">
              Stay Updated with MyFitness
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              Get exclusive workout tips, nutrition advice, and special offers delivered to your inbox.
            </p>

            {newsletterSuccess ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-500/20 border border-green-500 rounded-2xl p-8 max-w-md mx-auto"
              >
                <FaCheckCircle className="text-green-500 text-5xl mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">Successfully Subscribed!</h3>
                <p className="text-gray-300">
                  Thank you for joining our newsletter. Check your inbox for a welcome email.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="max-w-2xl mx-auto">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={newsletterName}
                    onChange={(e) => setNewsletterName(e.target.value)}
                    className="w-full px-6 py-4 rounded-xl bg-gray-800/50 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                  />
                  <input
                    type="email"
                    placeholder="Your Email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    required
                    className="w-full px-6 py-4 rounded-xl bg-gray-800/50 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                  />
                </div>
                
                {newsletterError && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-red-500 mb-4 text-sm"
                  >
                    {newsletterError}
                  </motion.p>
                )}

                <button
                  type="submit"
                  disabled={newsletterLoading}
                  className="w-full md:w-auto px-12 py-4 bg-gradient-to-r from-primary to-secondary text-white font-bold text-lg rounded-xl hover:shadow-2xl hover:shadow-primary/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {newsletterLoading ? 'Subscribing...' : 'Subscribe Now'}
                </button>

                <p className="text-gray-500 text-sm mt-4">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-r from-primary to-secondary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6 text-white">
              Ready to Transform Your Life?
            </h2>
            <p className="text-xl text-white/90 mb-12">
              Join 500,000+ users who are already crushing their fitness goals with MyFitness.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button className="bg-white text-black px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors flex items-center space-x-3">
                <FaApple size={24} />
                <div className="text-left">
                  <div className="text-xs">Download on the</div>
                  <div className="font-bold">App Store</div>
                </div>
              </button>
              <button className="bg-black text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-900 transition-colors flex items-center space-x-3">
                <FaGooglePlay size={24} />
                <div className="text-left">
                  <div className="text-xs">Get it on</div>
                  <div className="font-bold">Google Play</div>
                </div>
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
